import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { StatusChangeAlertComponent } from '@app/library/panes/my-library-content/alerts/status-change-alert/status-change-alert.component';
import { ContentService, FeaturesService, FilesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { IconComponent } from '@shared/icon/icon.component';
import { addDays } from 'date-fns';
import { Content, EContentPrivacy, FeatureEnum, ModerationStatusEnum } from 'lingo2-models';
import { CookieService } from 'ngx-cookie';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiCover, OnUiCoverModule } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';
import { i18nService as TranslateService } from '../../../../i18n/translate.service';

@Component({
  selector: 'app-library-content-mini',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    OnUiCoverModule,
    RouterModule,
    OnUiCoverModule,
    StatusChangeAlertComponent,
    IconComponent,
  ],
  templateUrl: './library-content-mini.component.html',
  styleUrls: ['./library-content-mini.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryContentMiniComponent extends ChangableComponent {
  private translate = inject(TranslateService);
  private contentService = inject(ContentService);
  private features = inject(FeaturesService);
  private cookie = inject(CookieService);
  deviceService = inject(DeviceDetectorService);

  public contentUrl: string;
  public editContentUrl: string;
  public coverUrl: OnUiCover;
  public contentTypeTitle: string;
  public level: string;
  public cutLength = 12;
  public isAlertOpened: boolean;
  public alertType: string;
  public action: string;

  public moderationStatuses = [
    {
      value: ModerationStatusEnum.default,
      label: 'library.user-service-card.status.on-moderation',
    },
    {
      value: ModerationStatusEnum.softDeclined,
      label: 'library.user-service-card.status.declined',
    },
    {
      value: ModerationStatusEnum.hardDeclined,
      label: 'library.user-service-card.status.declined',
    },
  ];

  @Input() set content(content: Content) {
    this._content = content;
    this.prepareCard();
  }

  public get content(): Content {
    return this._content;
  }

  @Output() clicked = new EventEmitter<{ type: string; content: Content }>();
  @Output() openLessonInModal = new EventEmitter<Content>();

  private _content: Content;

  public get isMobile() {
    return this.deviceService.isMobile();
  }

  public onAlertCancel() {
    this.isAlertOpened = false;
  }

  public onStatusChange() {
    switch (this.action) {
      case 'unpublish':
        this.onUnPublishContent();
        break;
      case 'archive':
        this.onDeleteContent();
        break;
      case 'restore':
        this.onRestoreContent();
        break;
    }
    this.cookie.put(this.alertType + '-alert', 'true', {
      expires: addDays(new Date(), 14), // запомнить на 14 дней
    });
  }

  public checkContentStatusChange(action: string) {
    this.action = action;
    this.alertType = action + '-' + this.status;

    if (this.cookie.get(this.alertType + '-alert')) {
      this.onStatusChange();
    } else {
      this.isAlertOpened = true;
    }
  }

  public onUnPublishContent() {
    this.contentService
      .unPublish(this.content?.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clicked.emit({ type: 'Unpublish', content: this.content });
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public onDeleteContent() {
    this.contentService
      .archive(this.content?.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clicked.emit({ type: 'Archive', content: this.content });
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public onEditContent() {
    window.open(this.editContentUrl, '_blank');
    this.isAlertOpened = false;
  }

  public onRestoreContent() {
    this.contentService
      .restore(this.content?.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.clicked.emit({ type: 'Restore', content: this.content });
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });
  }

  public get status(): string {
    if (!!this.content?.archived_at) {
      return 'archive';
    }
    if (!!this.content?.published_at && this.content.moderation_status === ModerationStatusEnum.approved) {
      return 'public';
    }
    if (this.content?.moderation_status === ModerationStatusEnum.default) {
      return 'on-moderation';
    }
    if (this.content?.privacy === EContentPrivacy.private) {
      return 'private';
    }
    return 'draft';
  }

  public canAction(action: string): boolean {
    switch (action) {
      case 'view':
        return this.status === 'public' || this.status === 'private' || this.status === 'on-moderation';
      case 'unpublish':
        return this.status === 'public' && this.content.privacy !== EContentPrivacy.private;
      case 'restore':
        return this.status === 'archive';
      case 'edit':
        return this.status === 'public' || this.status === 'private' || this.status === 'draft';
      case 'archive':
        return this.status === 'public' || this.status === 'private' || this.status === 'draft';
      default:
        return false;
    }
  }

  public onOpenCardInModal() {
    this.openLessonInModal.emit(this.content);
  }

  private prepareCard() {
    this.contentUrl = LibraryRouter.contentRouteUniversal(this.content);
    this.editContentUrl = '/constructor/edit/' + this.content.id;
    this.contentTypeTitle = ContentService.contentTypeTitle[this.content.type];
    if (!!this.content.level_other_name) {
      this.level = this.content.level_other_name;
    } else {
      this.level =
        'level' in this.content && this.content.level
          ? this.content.level.title
          : this.translate.directInstant('subject-levels.any-level');
    }

    this.coverUrl = {
      img: FilesService.getFileUrlBySize(this.content.cover_id),
      form: 'base',
      type: 'cover',
    };
    this.detectChanges();
  }

  public get moderationStatus() {
    return this.moderationStatuses.find((el) => el.value === this.content.moderation_status);
  }

  public get isAvailableClickAndInfo() {
    return this.features.isAvailable(FeatureEnum.statistics);
  }
}
